import { wsconnect } from '@nats-io/nats-core';
import { OnrampApiError } from './client.js';
/** Private hints only invalidate REST snapshots; they never carry balances or credentials. */
export function createOnrampRealtime(options) {
    const url = new URL(options.server);
    if (url.protocol !== 'wss:' &&
        !(url.protocol === 'ws:' &&
            (['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) ||
                url.hostname.endsWith('.localhost'))))
        throw new Error('onramp_requires_secure_origin');
    if (!options.subject || /[\s*>]/.test(options.subject))
        throw new Error('onramp_invalid_subject');
    const listeners = new Set();
    let disposed = false;
    let connection;
    let retry;
    let renewal;
    let failures = 0;
    let retryNotBefore = 0;
    const unregister = options.client.onDispose(dispose);
    void connect();
    return {
        subscribe(listener) {
            if (!disposed)
                listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dispose,
    };
    async function connect() {
        if (disposed)
            return;
        try {
            let credentials = await options.client.getRealtimeCredentials();
            if (disposed)
                return;
            validateCredentials(credentials);
            const candidate = await wsconnect({
                servers: [options.server],
                authenticator: () => ({ jwt: credentials.jwt }),
                maxReconnectAttempts: 3,
                reconnectTimeWait: 1000,
                timeout: 5000,
                pingInterval: 10000,
            });
            if (disposed) {
                await candidate.close();
                return;
            }
            connection = candidate;
            failures = 0;
            const subscription = candidate.subscribe(options.subject);
            await candidate.flush();
            options.onEvent?.({ kind: 'subscribed' });
            async function renew() {
                try {
                    const next = await options.client.getRealtimeCredentials();
                    if (disposed || connection !== candidate)
                        return;
                    validateCredentials(next);
                    credentials = next;
                    await candidate.reconnect();
                    scheduleRenewal();
                }
                catch (error) {
                    retainCooldown(error);
                    options.onError('onramp_realtime_unavailable');
                    await candidate.close();
                }
            }
            function scheduleRenewal() {
                clearTimeout(renewal);
                renewal = setTimeout(() => {
                    void renew();
                }, Math.max(1000, credentials.expiresAt * 1000 - Date.now() - 60000));
            }
            scheduleRenewal();
            try {
                for await (const message of subscription) {
                    if (disposed)
                        break;
                    let decoded;
                    try {
                        decoded = message.json();
                    }
                    catch {
                        options.onError('onramp_realtime_invalid_hint');
                        continue;
                    }
                    const id = operationIdFromHint(decoded, options.environment, options.network);
                    if (id === null) {
                        options.onError('onramp_realtime_invalid_hint');
                        continue;
                    }
                    options.onEvent?.({ kind: 'hint', operationId: id });
                    for (const listener of listeners)
                        listener(id);
                }
            }
            finally {
                clearTimeout(renewal);
                options.onEvent?.({ kind: 'disconnected' });
                await candidate.close();
                if (connection === candidate)
                    connection = undefined;
            }
        }
        catch (error) {
            retainCooldown(error);
            if (!disposed)
                options.onError('onramp_realtime_unavailable');
        }
        finally {
            if (!disposed) {
                failures += 1;
                retry = setTimeout(() => {
                    void connect();
                }, Math.max(retryNotBefore - Date.now(), Math.min(60000, 1000 * 2 ** Math.min(failures, 6))));
            }
        }
    }
    function retainCooldown(error) {
        if (error instanceof OnrampApiError)
            retryNotBefore = Math.max(retryNotBefore, Date.now() + error.retryAfterMs);
    }
    function validateCredentials(credentials) {
        if (credentials.kind !== 'user' ||
            !credentials.allow_sub.includes(options.subject) ||
            credentials.expiresAt * 1000 <= Date.now() + 1000)
            throw new Error('onramp_realtime_unauthorized');
    }
    function dispose() {
        if (disposed)
            return;
        disposed = true;
        clearTimeout(retry);
        clearTimeout(renewal);
        listeners.clear();
        void connection?.close();
        // onDispose can invoke synchronously when a host supplies an already-disposed client.
        queueMicrotask(() => unregister());
    }
}
export function operationIdFromHint(value, environment, network) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const hint = value;
    if (typeof hint.operationId !== 'string' ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(hint.operationId) ||
        typeof hint.version !== 'number' ||
        !Number.isSafeInteger(hint.version) ||
        hint.version < 1 ||
        hint.environment !== environment ||
        hint.network !== network)
        return null;
    return hint.operationId;
}
